/**
 * Check YouTube: does the live title and description match our output?
 * Browser-safe, pure.
 *
 *   exact: the live title equals one of the title options and the live
 *          description equals ours (after normalizing whitespace).
 *   close: the title is at high similarity to an option and the
 *          description matches at least 90%.
 *   none:  anything else.
 */

import { distance } from "fastest-levenshtein";
import type { PublishComparison } from "./types";

/** Title similarity at or above this counts as "close". */
export const PUBLISH_TITLE_CLOSE_SIMILARITY = 0.9;
/** Description similarity at or above this counts as "close". */
export const PUBLISH_DESCRIPTION_CLOSE_SIMILARITY = 0.9;

/** Collapse whitespace the way YouTube may (CRLF, trailing spaces, runs). */
export function normalizePublishedText(
	text: string | null | undefined,
): string {
	return (text ?? "")
		.replace(/\r\n?/g, "\n")
		.split("\n")
		.map((line) => line.replace(/[ \t]+/g, " ").trim())
		.join("\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}

/** 1 - normalized Levenshtein distance; 1 for two empty strings. */
export function textSimilarity(a: string, b: string): number {
	const x = normalizePublishedText(a);
	const y = normalizePublishedText(b);
	if (!x.length && !y.length) return 1;
	return 1 - distance(x, y) / Math.max(x.length, y.length);
}

export function comparePublishedVideo(
	ours: { titles: readonly string[]; description: string },
	live: { title: string; description: string },
): PublishComparison {
	const liveTitle = normalizePublishedText(live.title);
	let titleIndex: number | null = null;
	let titleSimilarity = 0;
	for (let i = 0; i < ours.titles.length; i++) {
		const s = textSimilarity(ours.titles[i] ?? "", liveTitle);
		if (s > titleSimilarity) {
			titleSimilarity = s;
			titleIndex = i;
		}
	}
	const titleExact =
		titleIndex !== null &&
		normalizePublishedText(ours.titles[titleIndex]) === liveTitle;
	const descriptionExact =
		normalizePublishedText(ours.description) ===
		normalizePublishedText(live.description);
	const descriptionSimilarity = descriptionExact
		? 1
		: textSimilarity(ours.description, live.description);

	let match: PublishComparison["match"] = "none";
	if (titleExact && descriptionExact) match = "exact";
	else if (
		titleSimilarity >= PUBLISH_TITLE_CLOSE_SIMILARITY &&
		descriptionSimilarity >= PUBLISH_DESCRIPTION_CLOSE_SIMILARITY
	) {
		match = "close";
	}
	return {
		match,
		titleIndex: titleSimilarity > 0 ? titleIndex : null,
		titleSimilarity: Math.round(titleSimilarity * 1000) / 1000,
		titleExact,
		descriptionSimilarity: Math.round(descriptionSimilarity * 1000) / 1000,
		descriptionExact,
	};
}
